import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import type StatusCode from "../../enums/httpStatusCodes";

export interface FailureHttpResponse {
  isSuccess: false;
  message: string;
  code: StatusCode;
}

export interface SuccessHttpResponse<T = unknown> {
  data: T;
  isSuccess: true;
  headers: AxiosResponse["headers"];
  code: StatusCode;
}

export type HttpResponse<T = unknown> = FailureHttpResponse | SuccessHttpResponse<T>;

interface IHttp {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<HttpResponse<T>>;
  post<T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<HttpResponse<T>>;
  head<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<HttpResponse<T>>;
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<HttpResponse<T>>;
}

const isAxiosError = <T = unknown>(error: unknown): error is AxiosError<T> => {
  return (error as AxiosError).response !== undefined;
};

const handler = async <T = unknown>(cb: () => Promise<AxiosResponse<T>>): Promise<HttpResponse<T>> => {
  try {
    const result = await cb();

    return {
      isSuccess: true,
      data: result.data,
      headers: result.headers,
      code: result.status,
    };
  } catch (error) {
    if (!isAxiosError(error)) {
      throw error;
    }

    return {
      isSuccess: false,
      code: error.response!.status,
      message: error.message,
    };
  }
};

const baseRequest = <T = unknown, D = unknown>(
  call: (url: string, config?: AxiosRequestConfig<D>) => Promise<AxiosResponse<T>>,
) => {
  return (url: string, config?: AxiosRequestConfig<D>): Promise<HttpResponse<T>> => handler(() => call(url, config));
};

const baseDataRequest = <T = unknown, D = unknown>(
  call: (url: string, data?: D, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>,
) => {
  return (url: string, data?: D, config?: AxiosRequestConfig): Promise<HttpResponse<T>> =>
    handler(() => call(url, data, config));
};

export const http: IHttp = {
  get: baseRequest(axios.get),
  head: baseRequest(axios.head),
  post: baseDataRequest(axios.post),
  delete: baseRequest(axios.delete),
};

export default http;
